import { uniq, uniqBy } from 'lodash'
import { arrayToObject } from '~/common/arrayUtils2'
import {
  CalculatedField,
  CustomFieldPath,
  DatasetRelation,
  Field,
  PathAndField,
  QueryFilter,
  SortDirection,
  LinkedUsages
} from '~/custom_reports/types'
import { ConditionGroupData } from '~/custom_reports/v3/state/StagingStateProvider'

// Gets the relations (sorted by level) from `datasetRelations` for each path
export const getNestedRelations = (
  paths: Array<string>,
  rootDataset: string,
  datasetRelations: Array<DatasetRelation>
) => {
  const relationsMap = arrayToObject(
    datasetRelations,
    r => r.path,
    r => r
  )

  const topLevelRelations = uniq(
    paths.filter(path => path !== CustomFieldPath.Calculated && path !== CustomFieldPath.Parameter)
  )
    .filter(path => path !== rootDataset)
    .map(path => relationsMap[path])

  const maxDepth = Math.max(...topLevelRelations.map(r => r.level), 0)
  let relations = topLevelRelations

  const relationByPathLookup = arrayToObject(
    datasetRelations,
    r => r.path,
    r => r
  )

  for (let level = maxDepth; level > 1; level--) {
    relations = uniqBy(
      [...relations, ...relations.filter(r => r.level === level).map(r => relationByPathLookup[r.path])],
      r => r.id
    ).filter(r => !!r)
  }
  return relations.sort((a, b) => a.level - b.level)
}

export const ifMatchThen = <T>(value: unknown, then: (value: T) => boolean): boolean =>
  (value as T) ? then(value as T) : false

export const generateQueryParams = ({
  search,
  filterTab
}: {
  search: string
  filterTab: string
}): Record<string, boolean | string> => {
  const update: Record<string, boolean | string> = {
    search,
    [filterTab]: true
  }
  return update
}

export const pathAndFieldKey = (pathAndField: PathAndField) => `${pathAndField.path}.${pathAndField.field}`

export const isFieldSelected = (selectedFields: Field[], pathAndField: PathAndField) =>
  selectedFields.find(f => f.path === pathAndField.path && f.field === pathAndField.field)

export const isFieldSorted = (sortFields: SortDirection[], pathAndField: PathAndField) =>
  sortFields.find(f => f.path === pathAndField.path && f.field === pathAndField.field)

export const isFieldFiltered = (filters: QueryFilter[], pathAndField: PathAndField) =>
  filters.find(f => f.path === pathAndField.path && f.field === pathAndField.field)

export const isFieldUsedInCalculatedFields = (calculatedFields: CalculatedField[], pathAndField: PathAndField) => {
  calculatedFields
    .flatMap(cf => {
      const fields = cf.fields
      if (fields as PathAndField[]) {
        return fields as PathAndField[]
      }
      if (fields as ConditionGroupData[]) {
        return (fields as ConditionGroupData[]).flatMap<PathAndField>(group => [
          ...group.statementFields.flatMap(statement => [
            statement as PathAndField,
            ...((statement.value_is_field ? [statement.value] : []) as PathAndField[])
          ]),
          ...((group.then_is_field ? [group.then] : []) as PathAndField[])
        ])
      }
      return []
    })
    .find(cf => cf?.path === pathAndField.path && cf.field === pathAndField.field)
}

export const getFlagDetails = (linkedUsages: LinkedUsages) => {
  const hasFlag =
    linkedUsages &&
    (linkedUsages.invoices.length > 0 ||
      linkedUsages.sftp_integration ||
      linkedUsages.api_integration ||
      linkedUsages.email_integration)

  return hasFlag
}

export const isNewReport = (reportId?: number | null) => reportId === null || reportId === undefined
